/**
 * Late Entry Requests API
 * Handles late entry request management
 */
import apiClient from './apiClient';

export interface LateEntryRequest {
  id: number;
  employee_id: number;
  employee_name: string | null;
  requested_date: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  handled_by: number | null;
  handler_name: string | null;
  handled_at: string | null;
  reject_reason: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface LateEntryRequestsListResponse {
  requests: LateEntryRequest[];
  total: number;
  page?: number;
  per_page?: number;
  pages?: number;
}

export interface LateEntryRequestsQueryParams {
  status?: string;
  page?: number;
  per_page?: number;
}

export const lateEntryRequestsApi = {
  /**
   * Get all late entry requests (filtered by role)
   */
  getRequests: async (params?: LateEntryRequestsQueryParams): Promise<LateEntryRequestsListResponse> => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.per_page) queryParams.append('per_page', String(params.per_page));

    const query = queryParams.toString();
    return apiClient.get<LateEntryRequestsListResponse>(`/late-entries${query ? `?${query}` : ''}`);
  },

  /**
   * Get current employee's late entry requests
   */
  getMyRequests: async (status?: string): Promise<LateEntryRequestsListResponse> => {
    const queryParams = new URLSearchParams();
    if (status) queryParams.append('status', status);
    
    const query = queryParams.toString();
    return apiClient.get<LateEntryRequestsListResponse>(`/late-entries/my-requests${query ? `?${query}` : ''}`);
  },

  /**
   * Get pending late entry requests (PMO/SuperAdmin only)
   */
  getPendingRequests: async (): Promise<LateEntryRequestsListResponse> => {
    return apiClient.get<LateEntryRequestsListResponse>('/late-entries/pending');
  },

  /**
   * Create a new late entry request
   */
  createRequest: async (data: {
    requested_date: string;
    reason: string;
  }): Promise<{ message: string; request: LateEntryRequest }> => {
    return apiClient.post('/late-entries', data);
  },

  /**
   * Approve a late entry request (PMO/SuperAdmin only)
   */
  approveRequest: async (requestId: number): Promise<{ message: string; request: LateEntryRequest }> => {
    return apiClient.put(`/late-entries/${requestId}/approve`);
  },

  /**
   * Reject a late entry request (PMO/SuperAdmin only)
   */
  rejectRequest: async (requestId: number, reason?: string): Promise<{ message: string; request: LateEntryRequest }> => {
    return apiClient.put(`/late-entries/${requestId}/reject`, { reason });
  },
};

export default lateEntryRequestsApi;
